import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, Crown, Zap, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const OFFERS = [
  { key: "boost_7d", icon: Zap, accent: "orange" },
  { key: "sponsor_monthly", icon: Sparkles, accent: "gold", popular: true },
  { key: "premium_monthly", icon: Crown, accent: "navy" },
];

// Card variants keep the JSX free of nested ternaries — one style bundle per
// accent, resolved once per card instead of 4 inline ternaries.
const PRICING_VARIANTS = {
  navy: {
    card: "bg-gradient-to-br from-[#1E3A8A] to-[#172554] text-white border border-[#1E3A8A]",
    iconBox: "bg-white/10",
    iconFg: "text-[#F97316]",
    price_suffix: "text-white/70",
    check: "text-[#F97316]",
    feat_text: "text-white/90",
    button: "bg-[#F97316] hover:bg-[#EA580C] text-white",
  },
  gold: {
    card: "bg-white border-2 border-[#F97316] shadow-lg shadow-[#F97316]/10",
    iconBox: "bg-[#FFF7ED]",
    iconFg: "text-[#F97316]",
    price_suffix: "text-slate-500",
    check: "text-green-600",
    feat_text: "text-slate-700",
    button: "bg-[#F97316] hover:bg-[#EA580C] text-white",
  },
  plain: {
    card: "bg-white border border-slate-200",
    iconBox: "bg-[#1E3A8A]/10",
    iconFg: "text-[#1E3A8A]",
    price_suffix: "text-slate-500",
    check: "text-green-600",
    feat_text: "text-slate-700",
    button: "bg-[#1E3A8A] hover:bg-[#172554] text-white",
  },
};

export default function Pricing() {
  const { user, lang } = useAuth();
  const nav = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [myCompanies, setMyCompanies] = useState([]);
  const [selectedCo, setSelectedCo] = useState("");
  const [busy, setBusy] = useState("");
  const [fx, setFx] = useState({ currency: "EUR", symbol: "€", fx_from_eur: 1.0, country: null });

  useEffect(() => {
    http.get("/payments/catalog").then((r) => setCatalog(r.data));
    if (user?.user_type === "company" || user?.user_type === "admin") {
      http.get("/companies/mine/list").then((r) => {
        setMyCompanies(r.data);
        if (r.data.length) setSelectedCo(r.data[0].id);
      });
    }
  }, [user]);

  // Refresh the FX estimate whenever the user picks a different company
  // (or falls back to their home_country). Price display is INFORMATIONAL —
  // actual Stripe billing stays in the base EUR currency.
  useEffect(() => {
    const co = myCompanies.find((x) => x.id === selectedCo);
    const country = co?.country || user?.home_country || "";
    http.get("/payments/currency", { params: { country } }).then((r) => setFx(r.data)).catch(() => {});
  }, [selectedCo, myCompanies, user?.home_country]);

  const fmt = (amountEur) => {
    const local = amountEur * (fx.fx_from_eur || 1);
    const rounded = fx.currency === "JPY" || fx.currency === "COP" ? Math.round(local) : local.toFixed(2);
    return `${fx.symbol}${rounded}`;
  };

  const buy = async (lookup_key, needsCompany) => {
    if (!user) { nav("/login"); return; }
    if (user.user_type !== "company" && user.user_type !== "admin") {
      toast.error(t(lang, "company_only"));
      return;
    }
    if (needsCompany && !selectedCo) {
      toast.error(t(lang, "choose_company"));
      return;
    }
    setBusy(lookup_key);
    try {
      const { data } = await http.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        company_id: needsCompany ? selectedCo : null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur paiement");
      setBusy("");
    }
  };

  if (!catalog) return <Layout><div className="p-10 text-center">…</div></Layout>;

  const isCompany = user?.user_type === "company" || user?.user_type === "admin";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#F97316] font-semibold mb-3">
            {lang === "fr" ? "Tarifs entreprise" : "Company pricing"}
          </span>
          <h1 data-testid="pricing-title" className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1E3A8A]">
            {t(lang, "pricing_title")}
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto leading-relaxed">{t(lang, "pricing_sub")}</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-200 text-green-800 text-sm">
            <CheckCircle2 className="w-4 h-4" /> {t(lang, "pricing_candidates_free")}
          </div>
        </div>

        {isCompany && myCompanies.length > 0 && (
          <div className="mt-8 max-w-md mx-auto p-4 rounded-2xl border border-slate-200 bg-white">
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "choose_company")}</Label>
            <Select value={selectedCo} onValueChange={setSelectedCo}>
              <SelectTrigger data-testid="pricing-select-co" className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {myCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.city}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {OFFERS.map(({ key, icon: Icon, accent, popular }) => {
            const c = catalog[key];
            const features = c[`features_${lang}`] || c.features_fr;
            const v = PRICING_VARIANTS[accent] || PRICING_VARIANTS.plain;
            return (
              <div
                key={key}
                data-testid={`pricing-card-${key}`}
                className={`relative rounded-3xl p-8 transition-transform hover:-translate-y-1 ${v.card}`}
              >
                {popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F97316] text-white rounded-full px-3">
                    {t(lang, "most_popular")}
                  </Badge>
                )}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${v.iconBox}`}>
                  <Icon className={`w-5 h-5 ${v.iconFg}`} />
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight">{c[`name_${lang}`] || c.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold" data-testid={`pricing-amount-${key}`}>{fmt(c.amount)}</span>
                  <span className={`text-sm ${v.price_suffix}`}>
                    {c.recurring ? t(lang, "per_month") : t(lang, "once")}
                  </span>
                </div>
                {fx.currency !== "EUR" && (
                  <p className={`text-[10px] mt-1 ${v.price_suffix}`}>
                    ≈ {c.amount.toFixed(0)}€ · {lang === "fr" ? "facturé en EUR" : "billed in EUR"}
                  </p>
                )}
                <ul className="mt-6 space-y-2.5">
                  {features.map((f) => (
                    <li key={`feat-${f}`} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${v.check}`} />
                      <span className={v.feat_text}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`pricing-buy-${key}`}
                  disabled={busy === key || (!isCompany && !!user)}
                  onClick={() => buy(key, key !== "premium_monthly")}
                  className={`mt-8 w-full h-11 rounded-full ${v.button}`}
                >
                  {busy === key ? "..." : (c.recurring ? t(lang, "subscribe") : t(lang, "buy_now"))}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                {!isCompany && user && (
                  <p className="text-xs text-center mt-3 text-slate-500">{t(lang, "company_only")}</p>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 text-center mt-12">
          {lang === "fr" ? "Mode test Stripe · Testez avec la carte 4242 4242 4242 4242. Aucun débit réel." : "Stripe test mode · Test with card 4242 4242 4242 4242. No real charge."}
        </p>
      </div>
    </Layout>
  );
}
